import { useTranslation } from 'react-i18next';
import ExoForm from '../exo/ExoForm';
import StarIcon from '@mui/icons-material/Star';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import Loader from '../special/Loader';
import { useState } from 'react';
import {
  API_DELETE,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../api/api';
import findDifferences from '../exo/findDifferencesInDataCollection';
import * as Yup from 'yup';
import DeleteIcon from '@mui/icons-material/Delete';
import ExoAlert from '../exo/ExoAlert';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../../global/theme/tokens';

export const NewCompanyLead = ({
  onClose,
  onCreate,
  onUpdate,
  startData,
  onFeedback,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const [reqDel, setReqDel] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const fields = [
    {
      label: t('Logo'),
      type: 'logo',
      key: 'logo',
    },
    {
      label: t('Company Name'),
      key: 'name',
      type: 'text',
    },
    {
      label: t('Company Name Short'),
      key: 'nameShort',
      type: 'text',
    },
    {
      label: t('Website'),
      key: 'url',
      type: 'url',
      allowScraping: false,
    },
    {
      label: t('Category'),
      key: 'categories',
      type: 'leadCategory',
    },
    {
      label: t('Note'),
      key: 'note',
      type: 'richText',
    },
    {
      label: t('Employee Count'),
      key: 'employeeCount',
      type: 'number',
    },
    {
      label: t('Responsible Employee'),
      key: 'responsibleUser',
      type: 'user',
    },
    {
      label: t('Address'),
      key: 'address',
      type: 'address',
    },
    {
      label: t('E-Mails'),
      type: 'nameValueArray',
      key: 'emails',
      addLabel: t('Add E-Mail'),
      nameKey: 'emailType',
      valueKey: 'emailAddress',
      placeholder: {
        name: t('Label'),
        value: t('E-Mail'),
      },
      radioItems: [
        {
          key: 'isPrimary',
          Icon: StarIcon,
          label: 'Favorite',
        },
        {
          key: 'isInvoiceMail',
          Icon: ReceiptIcon,
          label: 'Invoice E-Mail',
        },
      ],
    },
    {
      label: t('Phonenumbers'),
      type: 'nameValueArray',
      key: 'telephoneNumbers',
      addLabel: t('Add phonenumber'),
      nameKey: 'phoneType',
      valueKey: 'phoneNumber',
      placeholder: {
        name: t('Label'),
        value: t('Phonenumber'),
      },
      radioItems: [
        {
          key: 'isPrimary',
          Icon: StarIcon,
          label: 'Favorite',
        },
      ],
    },
    {
      label: t('Purchasing profile'),
      key: 'properties',
      type: 'salesOjectProperties',
    },
    {
      label: t('Contact Persons'),
      key: 'persons',
      type: 'leadPersons',
    },
    {
      label: t('Managers'),
      key: 'managingPersons',
      type: 'leadPersons',
    },
  ];

  const config = useConfig();

  function handleSubmit(data) {
    data.properties = (data.properties || []).map((prop) => {
      // checkbox and no minValue is set, assume it's unchecked
      if (prop.type === 'checkbox') {
        return {
          ...prop,
          minValue: prop.minValue ?? '0', // default to "0" if undefined
        };
      }
      return prop;
    });

    const newData = {
      ...findDifferences(startData, data),
      emails: data.emails,
      phoneNumbers: data.phoneNumbers,
      properties: data.properties,
    };

    DBRequest({
      config,
      path:
        startData && startData.id
          ? `leads/companies/${data.id}`
          : 'leads/companies',
      method: startData && startData.id ? API_PATCH(newData) : API_POST(data),
      onResponse: handleResponse,
      onFeedback,
      onLoading: setIsLoading,
    });
  }

  function handleResponse(data) {
    onClose();
    if (startData && startData.id) {
      onUpdate(data);
    } else {
      onCreate(data);
    }
  }

  const phoneSchema = Yup.object().shape({
    phoneType: Yup.string().required('Is required'),
    phoneNumber: Yup.string().required('Is required'),
  });
  const emailSchema = Yup.object().shape({
    emailType: Yup.string().required('Is required'),
    emailAddress: Yup.string().required('Is required'),
  });

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('Is required')),
    telephoneNumbers: Yup.array().of(phoneSchema).nullable(),
    emails: Yup.array().of(emailSchema).nullable(),
    responsibleUser: Yup.object().shape({
      id: Yup.number().required(t('Is required')),
    }),
    url: Yup.string().url(t('Must be a valid URL')).nullable(),
  });

  function handleDelete(element) {
    DBRequest({
      config,
      path: `leads/companies/${element.id}`,
      method: API_DELETE,
      onResponse: navigate(`/leads`),
      onFeedback,
    });
  }

  return (
    <Box
      className="flex flex-col w-full gap-2"
      sx={{
        '& .delButton': {
          transition: '0.3s ease',
          bgcolor: colors.error + '30',
          color: colors.error,
          border: `1px solid ${colors.error}80`,
          opacity: 1,
          transform: 'translate(0px)',
        },
        ':hover': {
          '& .editButton, & .delButton': {
            opacity: 1,
            transform: 'translate(0px)',
          },
        },
      }}
    >
      {/* Header row */}
      <Box
        className="flex items-center justify-between w-full"
        sx={{
          position: 'relative',
        }}
      >
        <Typography
          variant="h3"
          className="py-2"
          sx={(theme) => ({
            textAlign: 'center',
            width: '100%',
          })}
        >
          {t('New Lead Company')}
        </Typography>

        {/* delete button - only show when editing lead*/}
        {startData && startData.id && (
          <IconButton
            onClick={() => setReqDel(startData)}
            size="small"
            className="delButton"
            sx={{
              position: 'absolute',
              right: 40,
              top: '10%',
              transform: 'translateY(-50%)',
            }}
          >
            <DeleteIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        )}
      </Box>

      <Loader active={isLoading} />

      <ExoForm
        fields={fields}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        onCancel={onClose}
        startDataCollection={startData}
        noDifferences
        draftType="companyLead"
        titleFunction={(data) => data.nameShort || data.name || ''}
        outerClassName="pb-5 flex flex-col gap-4 h-full overflow-y-auto"
      />

      <ExoAlert
        onSubmit={() => handleDelete(reqDel)}
        open={Boolean(reqDel)}
        onClose={() => setReqDel(null)}
        alert={{
          submitLabel: t('Delete'),
          message: t('Are you sure you want to permanently delete this Lead?'),
        }}
      />
    </Box>
  );
};
